/* eslint-disable no-nested-ternary */
import { Card } from '@pmi/dsm-react';
import {
  estimatedQuantityTitle,
  isPayByUser,
} from '../../../../shared/constants';
import { AgreementApiModel } from '../../../../shared/models/DealModel';
import { DiscountType } from '../../../../shared/models/ProductModel';
import formatCurrency from '../../../../shared/utils/formatCurrency';
import getNetQuantity from '../../../../shared/utils/getNetQuantity';
import dealProductTableStyles from '../DealProductsTable/dealProductsTable.module.scss';
import styles from './dealProductCards.module.scss';

interface DealProductCardsProps {
  deal: AgreementApiModel;
}

function LabelValue({
  label,
  value,
  hasDiscount,
}: {
  label: string;
  value: string;
  // eslint-disable-next-line react/require-default-props
  hasDiscount?: boolean;
}) {
  return (
    <div className="d-flex flex-column">
      <span className={`${styles.labelValue} ${styles.label}`}>{label}:</span>
      <span
        className={`${styles.labelValue} ${
          hasDiscount ? 'text-decoration-line-through' : ''
        }`}
      >
        {value}
      </span>
    </div>
  );
}

function getNormalizedCount(value: number) {
  return Math.max(0, value);
}

function getClampedPercentage(value: number) {
  return Math.min(100, Math.max(0, Math.round(value)));
}

export default function DealProductCards({ deal }: DealProductCardsProps) {
  const products = deal.products ?? [];
  const payByUser = isPayByUser(deal.paymentArrangement);
  return (
    <div className="d-flex flex-column mb-3">
      {products?.map((p) => {
        const {
          discount,
          discountType,
          quantityAssigned,
          quantityRemaining,
          quantityRedeemed,
          quantityNotRedeemed,
        } = p;
        const discountValue =
          discountType === DiscountType.Percentage
            ? `${discount}% discount`
            : discountType === DiscountType.Amount
            ? `${discount} off`
            : 'None';
        const hasDiscount = discount > 0;

        const normalizedQuantityAssigned = getNormalizedCount(quantityAssigned);
        const normalizedQuantityNotAssigned =
          getNormalizedCount(quantityRemaining);
        const quantityAssignedTotal =
          normalizedQuantityAssigned + normalizedQuantityNotAssigned;
        const quantityAssignedPercentage = payByUser
          ? 100
          : getClampedPercentage(
              quantityAssignedTotal === 0
                ? 0
                : (normalizedQuantityNotAssigned / quantityAssignedTotal) * 100,
            );

        const normalizedQuantityRedeemed = getNormalizedCount(quantityRedeemed);
        const normalizedQuantityNotRedeemed =
          getNormalizedCount(quantityNotRedeemed);
        const quantityRedeemedTotal =
          normalizedQuantityRedeemed + normalizedQuantityNotRedeemed;
        const quantityRedeemedPercentage = payByUser
          ? 0
          : getClampedPercentage(
              quantityRedeemedTotal === 0
                ? 0
                : (normalizedQuantityRedeemed / quantityRedeemedTotal) * 100,
            );

        return (
          <Card className={`mt-3 ${styles.card}`} key={p.name}>
            <div className="d-flex flex-column m-4">
              <div className="d-flex flex-column mb-2">
                <span
                  className={styles.productName}
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: p.name }}
                />
                <div className={styles.sku}>{p.sku}</div>
              </div>
              <div className="row row-cols-2 gy-1">
                <div className="col">
                  <LabelValue
                    label="Price"
                    value={`$${formatCurrency(p.priceBase)}`}
                    hasDiscount={hasDiscount}
                  />
                  {hasDiscount && (
                    <>
                      <div className={styles.labelValue}>{discountValue}</div>
                      <div className={styles.labelValue}>{`$${formatCurrency(
                        p.price,
                      )}`}</div>
                    </>
                  )}
                </div>
                <div className="col">
                  <LabelValue
                    label={estimatedQuantityTitle(deal.paymentArrangement)}
                    value={`${getNetQuantity(p)}`}
                  />
                </div>
              </div>
              <hr className={styles.sep} />
              <div className="row row-cols-2">
                <div className="col">
                  <LabelValue label="Assigned" value={`${quantityAssigned}`} />
                  <div className={dealProductTableStyles.productDetailsRow}>
                    <div className={`${dealProductTableStyles.outer}`}>
                      <div
                        style={{ width: `${quantityAssignedPercentage}%` }}
                        className={dealProductTableStyles.inner}
                      />
                    </div>
                    <div className={dealProductTableStyles.smallText}>
                      {payByUser ? '∞' : p.quantityRemaining} Available
                    </div>
                  </div>
                </div>
                <div className="col">
                  <LabelValue label="Redeemed" value={`${quantityRedeemed}`} />
                  <div className={dealProductTableStyles.productDetailsRow}>
                    <div className={`${dealProductTableStyles.outer}`}>
                      <div
                        style={{ width: `${quantityRedeemedPercentage}%` }}
                        className={dealProductTableStyles.inner}
                      />
                    </div>
                    <div className={dealProductTableStyles.smallText}>
                      {payByUser ? '∞' : quantityNotRedeemed} Available
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
